import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import UserMenu from "@/components/UserMenu";
import AdminCareRequests from "@/components/AdminCareRequests";
import { 
  Users,
  UserCheck,
  FileText,
  AlertTriangle,
  TrendingUp,
  Euro,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Loader2,
  ShieldCheck,
  RefreshCw,
  Trash2,
  Key,
  Search,
  User,
  Stethoscope,
  Shield,
  MapPin,
  Building2,
  Star,
  Pencil,
  Download,
  History,
  Mail,
  Phone
} from "lucide-react";

const LOGO_URL = "/logo-infirmiers.png";

const AdminDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [pendingNurses, setPendingNurses] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [verifyingRpps, setVerifyingRpps] = useState(false);
  const [userFilter, setUserFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [resetPasswordModal, setResetPasswordModal] = useState(null);
  const [revenueModal, setRevenueModal] = useState(false);
  const [revenueDetails, setRevenueDetails] = useState(null);
  const [loadingRevenue, setLoadingRevenue] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [actionLoading, setActionLoading] = useState(null);
  const [editRatingModal, setEditRatingModal] = useState(null);
  const [editRatingValue, setEditRatingValue] = useState(5);
  const [editRatingComment, setEditRatingComment] = useState("");
  const [rppsChecks, setRppsChecks] = useState([]);
  const [rppsFilter, setRppsFilter] = useState("all");
  const [detailModal, setDetailModal] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [exportingUsers, setExportingUsers] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch KPIs
      const kpisRes = await fetch(`${API}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (kpisRes.ok) {
        const kpisData = await kpisRes.json();
        setKpis(kpisData.kpis);
      }

      // Fetch pending nurses
      const nursesRes = await fetch(`${API}/admin/nurses/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (nursesRes.ok) {
        const nursesData = await nursesRes.json();
        setPendingNurses(nursesData.nurses || []);
      }

      // Fetch disputes
      const disputesRes = await fetch(`${API}/disputes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (disputesRes.ok) {
        const disputesData = await disputesRes.json();
        setDisputes(disputesData.disputes || []);
      }

      // Fetch all users
      const usersRes = await fetch(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setAllUsers(usersData.users || []);
      }

      // Fetch all ratings
      const ratingsRes = await fetch(`${API}/admin/ratings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (ratingsRes.ok) {
        const ratingsData = await ratingsRes.json();
        setRatings(ratingsData.ratings || []);
      }

      // Fetch RPPS audit log
      const rppsRes = await fetch(`${API}/admin/rpps-checks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (rppsRes.ok) {
        const rppsData = await rppsRes.json();
        setRppsChecks(rppsData.checks || []);
      }
    } catch (error) {
      console.error("Error fetching admin data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyNurse = async (nurseId, approved) => {
    try {
      const response = await fetch(`${API}/admin/nurses/${nurseId}/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ approved, reason: approved ? "" : "Documents incomplets" })
      });

      if (response.ok) {
        fetchData();
      }
    } catch (error) {
      console.error("Error verifying nurse:", error);
    }
  };

  const handleVerifyRpps = async () => {
    setVerifyingRpps(true);
    try {
      const response = await fetch(`${API}/admin/verify-rpps`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Erreur");

      if (data.status === "already_running") {
        toast.info("Une vérification RPPS est déjà en cours…");
      } else {
        toast.info("Vérification RPPS lancée en arrière-plan…");
      }

      // Poll the status endpoint until the sweep finishes (avoids 502 on slow ANS API)
      const started = Date.now();
      const poll = async () => {
        try {
          const stRes = await fetch(`${API}/admin/verify-rpps/status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const st = await stRes.json();
          if (st.running && Date.now() - started < 5 * 60 * 1000) {
            setTimeout(poll, 3000);
            return;
          }
          // Finished (or timed out) — show recap
          if (st.summary) {
            const s = st.summary;
            const flaggedCount = st.flagged_count || 0;
            if (flaggedCount > 0) {
              toast.warning(`${s.checked} RPPS vérifiés — ${flaggedCount} anomalie(s) : ${s.inactive} radié(s)/inactif(s), ${s.not_found} introuvable(s)`);
            } else {
              toast.success(`${s.checked} RPPS vérifiés — tous valides ✅`);
            }
          } else if (st.error) {
            toast.error(`Erreur lors de la vérification : ${st.error}`);
          }
          fetchData();
          setVerifyingRpps(false);
        } catch (e) {
          setVerifyingRpps(false);
        }
      };
      setTimeout(poll, 3000);
    } catch (error) {
      toast.error(error.message);
      setVerifyingRpps(false);
    }
  };

  const getRppsBadge = (status) => {
    const map = {
      valid: { cls: "bg-[#00E073]/20 text-[#00E073]", label: "RPPS vérifié" },
      not_found: { cls: "bg-amber-500/20 text-amber-400", label: "RPPS introuvable" },
      inactive: { cls: "bg-red-500/20 text-red-400", label: "RPPS radié/inactif" },
      error: { cls: "bg-[#2A3A4D] text-[#94A3B8]", label: "RPPS non vérifié" }
    };
    const m = map[status] || { cls: "bg-[#2A3A4D] text-[#94A3B8]", label: "RPPS non vérifié" };
    return <span className={`px-2 py-0.5 rounded text-xs ${m.cls}`} data-testid={`rpps-badge-${status || 'none'}`}>{m.label}</span>;
  };

  const handleResolveDispute = async (disputeId, status, outcome = null) => {
    const labels = {
      refund_patient: "Rembourser le patient et annuler la prestation ?",
      release_nurse: "Libérer les fonds vers l'infirmier (litige rejeté) ?"
    };
    if (outcome && !window.confirm(labels[outcome])) return;
    setActionLoading(disputeId);
    try {
      const response = await fetch(`${API}/admin/disputes/${disputeId}/resolve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status, resolution: `Traité par admin`, refund_amount: 0, outcome })
      });

      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        const actionMsg = {
          refunded: "Patient remboursé",
          released: "Fonds libérés vers l'infirmier",
          refund_failed: "Échec du remboursement Stripe",
          release_failed: "Échec du transfert Stripe"
        }[data.escrow_action];
        toast.success(actionMsg ? `Litige traité — ${actionMsg}` : "Litige traité");
        fetchData();
      } else {
        toast.error(data.detail || "Erreur lors du traitement du litige");
      }
    } catch (error) {
      toast.error("Erreur lors du traitement du litige");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le compte de "${userName}" ?\n\nCette action est irréversible.`)) {
      return;
    }

    setActionLoading(userId);
    try {
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Erreur lors de la suppression");
      }

      toast.success("Compte supprimé avec succès");
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const openEditRating = (rating) => {
    setEditRatingValue(rating.rating || 5);
    setEditRatingComment(rating.comment || "");
    setEditRatingModal(rating);
  };

  const handleUpdateRating = async () => {
    if (!editRatingModal) return;
    if (editRatingValue < 1 || editRatingValue > 5) {
      toast.error("La note doit être entre 1 et 5");
      return;
    }
    setActionLoading(editRatingModal.rating_id);
    try {
      const response = await fetch(`${API}/admin/ratings/${editRatingModal.rating_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ rating: editRatingValue }),
      });
      if (!response.ok) {
        const d = await response.json();
        throw new Error(d.detail || "Erreur");
      }
      toast.success("Notation mise à jour");
      setEditRatingModal(null);
      fetchData();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteRating = async (rating) => {
    if (!window.confirm(`Supprimer la notation de ${rating.patient_name} (${rating.rating}★) ?\n\nCette action est irréversible.`)) {
      return;
    }
    setActionLoading(rating.rating_id);
    try {
      const response = await fetch(`${API}/admin/ratings/${rating.rating_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const d = await response.json();
        throw new Error(d.detail || "Erreur");
      }
      toast.success("Notation supprimée");
      fetchData();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleViewIdentityDocument = async (nurse) => {
    if (!nurse.identity_document_file) {
      toast.error("Aucune pièce d'identité jointe");
      return;
    }
    try {
      const response = await fetch(`${API}/nurses/identity-document/${nurse.user_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Téléchargement impossible");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleViewRcpDocument = async (nurse) => {
    if (!nurse.rcp_document_file) {
      toast.error("Aucune attestation RCP jointe");
      return;
    }
    try {
      const response = await fetch(`${API}/nurses/rcp-document/${nurse.user_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Téléchargement impossible");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const openRevenueModal = async () => {
    setRevenueModal(true);
    setLoadingRevenue(true);
    try {
      const res = await fetch(`${API}/admin/revenue-details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setRevenueDetails(await res.json());
      } else {
        toast.error("Impossible de charger le détail des revenus");
      }
    } catch (e) {
      toast.error("Erreur lors du chargement des revenus");
    } finally {
      setLoadingRevenue(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    setActionLoading(resetPasswordModal.user_id);
    try {
      const response = await fetch(`${API}/admin/users/${resetPasswordModal.user_id}/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ new_password: newPassword })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Erreur lors de la réinitialisation");
      }

      toast.success(`Mot de passe réinitialisé pour ${resetPasswordModal.name || resetPasswordModal.email}`);
      setResetPasswordModal(null);
      setNewPassword("");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleExportUsers = async () => {
    setExportingUsers(true);
    try {
      const response = await fetch(`${API}/admin/users/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || "Erreur lors de l'export");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `utilisateurs_infirmiersprives_${today}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.success("Export Excel téléchargé");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setExportingUsers(false);
    }
  };

  const openUserDetail = async (u) => {
    setLoadingDetail(true);
    setDetailModal({ loading: true, user_id: u.user_id, name: u.name, email: u.email });
    try {
      const response = await fetch(`${API}/admin/users/${u.user_id}/full`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Erreur lors du chargement du profil");
      }
      const data = await response.json();
      setDetailModal(data);
    } catch (error) {
      toast.error(error.message);
      setDetailModal(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  const openIdentityDoc = async (userId) => {
    try {
      const response = await fetch(`${API}/nurses/identity-document/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Pièce d'identité indisponible");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openRcpDoc = async (userId) => {
    try {
      const response = await fetch(`${API}/nurses/rcp-document/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Attestation RCP indisponible");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Filter users
  const filteredUsers = allUsers.filter(u => {
    const matchesType = userFilter === "all" || u.user_type === userFilter;
    const matchesSearch = !searchQuery || 
      u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D1C32] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00E073] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D1C32]">
      {/* Navigation */}
      <nav className="bg-[#1F2A38] border-b border-[#2A3A4D]">
        <div className="container-ip">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <img src={LOGO_URL} alt="Infirmiers Privés" className="h-10" />
              </Link>
              <span className="text-[#00E073] text-sm font-medium bg-[#00E073]/10 px-2 py-1 rounded">
                Admin
              </span>
            </div>
            
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={fetchData}
                className="text-[#94A3B8] hover:text-white"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              <UserMenu userName={user?.email} />
            </div>
          </div>
        </div>
      </nav>

      <main className="container-ip py-8">
        <h1 className="text-2xl font-bold text-white mb-8">Back-Office Administrateur</h1>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto">
          {[
            { id: "overview", label: "Vue d'ensemble", icon: TrendingUp },
            { id: "users", label: "Utilisateurs", icon: Users, badge: allUsers.length },
            { id: "interventions", label: "Demandes", icon: FileText },
            { id: "ratings", label: "Notations", icon: Star, badge: ratings.length },
            { id: "verifications", label: "Vérifications", icon: UserCheck, badge: pendingNurses.length },
            { id: "rpps-audit", label: "Audit RPPS", icon: History },
            { id: "disputes", label: "Litiges", icon: AlertTriangle, badge: disputes.filter(d => d.status === "open").length }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? "bg-[#00E073] text-[#0D1C32]"
                  : "bg-[#1F2A38] text-[#94A3B8] hover:bg-[#2A3A4D]"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.badge > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  activeTab === tab.id ? "bg-[#0D1C32] text-[#00E073]" : "bg-[#2A3A4D] text-white"
                }`}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && kpis && (
          <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="kpi-card">
                <Users className="w-6 h-6 text-[#00E073] mb-2" />
                <div className="kpi-value">{kpis.total_patients}</div>
                <div className="kpi-label">Patients</div>
              </div>
              <div className="kpi-card">
                <UserCheck className="w-6 h-6 text-[#28CAD4] mb-2" />
                <div className="kpi-value text-[#28CAD4]">{kpis.active_nurses}</div>
                <div className="kpi-label">Infirmiers actifs</div>
              </div>
              <div className="kpi-card">
                <FileText className="w-6 h-6 text-yellow-500 mb-2" />
                <div className="kpi-value text-yellow-500">{kpis.total_requests}</div>
                <div className="kpi-label">Demandes totales</div>
              </div>
              <button
                type="button"
                onClick={openRevenueModal}
                className="kpi-card text-left transition-all hover:border-[#00E073]/50 hover:bg-[#00E073]/5 cursor-pointer"
                data-testid="revenue-kpi-card"
              >
                <Euro className="w-6 h-6 text-[#00E073] mb-2" />
                <div className="kpi-value">{kpis.total_revenue?.toFixed(2)} €</div>
                <div className="kpi-label flex items-center gap-1">
                  Revenus (frais) <Eye className="w-3 h-3" />
                </div>
              </button>
            </div>

            {/* Secondary KPIs */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="card-dark p-6">
                <h3 className="text-sm text-[#94A3B8] mb-4">Taux de complétion</h3>
                <div className="flex items-end gap-4">
                  <div className="text-3xl font-bold text-[#00E073]">{kpis.completion_rate}%</div>
                  <div className="flex-1">
                    <div className="h-2 bg-[#2A3A4D] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[#00E073] rounded-full"
                        style={{ width: `${kpis.completion_rate}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-dark p-6">
                <h3 className="text-sm text-[#94A3B8] mb-4">Taux d'annulation</h3>
                <div className="flex items-end gap-4">
                  <div className="text-3xl font-bold text-red-400">{kpis.cancellation_rate}%</div>
                  <div className="flex-1">
                    <div className="h-2 bg-[#2A3A4D] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-red-400 rounded-full"
                        style={{ width: `${kpis.cancellation_rate}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-dark p-6">
                <h3 className="text-sm text-[#94A3B8] mb-4">Infirmiers actifs</h3>
                <div className="flex items-end gap-4">
                  <div className="text-3xl font-bold text-[#28CAD4]">{kpis.active_nurses}</div>
                  <div className="text-sm text-[#64748B]">
                    sur {kpis.total_nurses} inscrits
                  </div>
                </div>
              </div>
            </div>

            {/* Alerts */}
            <div className="card-dark p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Alertes</h3>
              <div className="space-y-3">
                {kpis.pending_verifications > 0 && (
                  <div className="flex items-center gap-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                    <Clock className="w-5 h-5 text-yellow-500" />
                    <span className="text-white">
                      {kpis.pending_verifications} infirmier(s) en attente de vérification
                    </span>
                  </div>
                )}
                {kpis.open_disputes > 0 && (
                  <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    <span className="text-white">
                      {kpis.open_disputes} litige(s) ouvert(s) à traiter
                    </span>
                  </div>
                )}
                {kpis.pending_verifications === 0 && kpis.open_disputes === 0 && (
                  <div className="flex items-center gap-3 bg-[#00E073]/10 border border-[#00E073]/30 rounded-lg p-3">
                    <CheckCircle className="w-5 h-5 text-[#00E073]" />
                    <span className="text-white">
                      Aucune action urgente requise
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  placeholder="Rechercher par nom ou email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-[#1F2A38] border-[#2A3A4D] text-white"
                />
              </div>
              <div className="flex gap-2">
                {[
                  { id: "all", label: "Tous", icon: Users },
                  { id: "patient", label: "Patients", icon: User },
                  { id: "nurse", label: "Infirmiers", icon: Stethoscope },
                  { id: "admin", label: "Admins", icon: Shield }
                ].map((filter) => (
                  <button
                    key={filter.id}
                    onClick={() => setUserFilter(filter.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      userFilter === filter.id
                        ? "bg-[#00E073] text-[#0D1C32]"
                        : "bg-[#1F2A38] text-[#94A3B8] hover:bg-[#2A3A4D]"
                    }`}
                  >
                    <filter.icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{filter.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Users List */}
            <div className="card-dark p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">
                  {filteredUsers.length} utilisateur(s)
                </h2>
                <Button
                  onClick={handleExportUsers}
                  disabled={exportingUsers}
                  className="bg-[#00E073] text-[#0D1C32] hover:bg-[#00c765] font-medium"
                  data-testid="export-users-excel-btn"
                >
                  {exportingUsers ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Exporter en Excel
                </Button>
              </div>
              
              {filteredUsers.length === 0 ? (
                <div className="empty-state">
                  <Users className="empty-state-icon" />
                  <p className="empty-state-text">Aucun utilisateur trouvé</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredUsers.map((u) => (
                    <div 
                      key={u.user_id}
                      className="bg-[#0D1C32] rounded-xl p-4 border border-[#2A3A4D] hover:border-[#00E073]/30 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4">
                          {/* Avatar */}
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            u.user_type === "nurse" ? "bg-[#28CAD4]/20" :
                            u.user_type === "admin" ? "bg-[#00E073]/20" : "bg-[#2A3A4D]"
                          }`}>
                            {u.picture ? (
                              <img src={u.picture} alt={u.name} className="w-12 h-12 rounded-full object-cover" />
                            ) : (
                              u.user_type === "nurse" ? <Stethoscope className="w-6 h-6 text-[#28CAD4]" /> :
                              u.user_type === "admin" ? <Shield className="w-6 h-6 text-[#00E073]" /> :
                              <User className="w-6 h-6 text-[#94A3B8]" />
                            )}
                          </div>
                          
                          {/* Info */}
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-medium text-white">{u.name || "Sans nom"}</h3>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                u.user_type === "nurse" ? "bg-[#28CAD4]/20 text-[#28CAD4]" :
                                u.user_type === "admin" ? "bg-[#00E073]/20 text-[#00E073]" :
                                "bg-[#2A3A4D] text-[#94A3B8]"
                              }`}>
                                {u.user_type === "nurse" ? "Infirmier" :
                                 u.user_type === "admin" ? "Admin" : "Patient"}
                              </span>
                            </div>
                            <p className="text-sm text-[#94A3B8] flex items-center gap-1.5">
                              <Mail className="w-3.5 h-3.5 text-[#64748B]" /> {u.email}
                            </p>
                            <p className="text-sm text-[#94A3B8] flex items-center gap-1.5 mt-0.5">
                              <Phone className="w-3.5 h-3.5 text-[#64748B]" /> {u.phone || "Non renseigné"}
                            </p>
                            
                            {/* Nurse specific info */}
                            {u.user_type === "nurse" && u.nurse_profile && (
                              <div className="mt-2 space-y-1">
                                <div className="flex items-center gap-2 text-xs">
                                  <span className={`px-2 py-0.5 rounded ${
                                    u.nurse_profile.is_active 
                                      ? "bg-[#00E073]/20 text-[#00E073]" 
                                      : "bg-yellow-500/20 text-yellow-500"
                                  }`}>
                                    {u.nurse_profile.is_active ? "Actif" : "En attente"}
                                  </span>
                                  {u.nurse_profile.rpps_number && (
                                    <span className="text-[#64748B]">RPPS: {u.nurse_profile.rpps_number}</span>
                                  )}
                                  {u.nurse_profile.rpps_number && getRppsBadge(u.nurse_profile.rpps_status)}
                                </div>
                                {u.nurse_profile.zones?.length > 0 && (
                                  <div className="flex items-center gap-1 text-xs text-[#64748B]">
                                    <MapPin className="w-3 h-3" />
                                    {u.nurse_profile.zones.slice(0, 3).map(z => 
                                      typeof z === 'string' ? z : z.city
                                    ).join(", ")}
                                  </div>
                                )}
                                {u.nurse_profile.business_info?.siret && (
                                  <div className="flex items-center gap-1 text-xs text-[#64748B]">
                                    <Building2 className="w-3 h-3" />
                                    SIRET: {u.nurse_profile.business_info.siret}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {u.created_at && (
                              <p className="text-xs text-[#64748B] mt-2">
                                Inscrit le {new Date(u.created_at).toLocaleDateString('fr-FR')}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-[#00E073]/40 text-[#00E073] hover:bg-[#00E073]/10"
                            onClick={() => openUserDetail(u)}
                            data-testid={`user-details-btn-${u.user_id}`}
                            title="Voir tout le profil"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {u.user_type !== "admin" && (
                            <>
                              <Button 
                                size="sm"
                                variant="outline"
                                className="border-[#2A3A4D] text-[#94A3B8] hover:bg-[#2A3A4D] hover:text-white"
                                onClick={() => setResetPasswordModal(u)}
                                disabled={actionLoading === u.user_id}
                              >
                                <Key className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm"
                                variant="outline"
                                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                                onClick={() => handleDeleteUser(u.user_id, u.name || u.email)}
                                disabled={actionLoading === u.user_id}
                              >
                                {actionLoading === u.user_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Reset Password Modal */}
            {resetPasswordModal && (
              <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                <div className="bg-[#1F2A38] rounded-xl p-6 max-w-md w-full border border-[#2A3A4D]">
                  <h3 className="text-lg font-semibold text-white mb-4">
                    Réinitialiser le mot de passe
                  </h3>
                  <p className="text-sm text-[#94A3B8] mb-4">
                    Définir un nouveau mot de passe pour <strong className="text-white">{resetPasswordModal.name || resetPasswordModal.email}</strong>
                  </p>
                  
                  <Input
                    type="password"
                    placeholder="Nouveau mot de passe (min. 6 caractères)"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="bg-[#0D1C32] border-[#2A3A4D] text-white mb-4"
                  />
                  
                  <div className="flex gap-3 justify-end">
                    <Button 
                      variant="outline"
                      className="border-[#2A3A4D] text-[#94A3B8]"
                      onClick={() => {
                        setResetPasswordModal(null);
                        setNewPassword("");
                      }}
                    >
                      Annuler
                    </Button>
                    <Button 
                      className="bg-[#00E073] text-[#0D1C32] hover:bg-[#00B85C]"
                      onClick={handleResetPassword}
                      disabled={actionLoading === resetPasswordModal.user_id}
                    >
                      {actionLoading === resetPasswordModal.user_id ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Key className="w-4 h-4 mr-2" />
                      )}
                      Réinitialiser
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* User Full Profile Detail Modal */}
        {detailModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="user-detail-modal">
            <div className="bg-[#1F2A38] rounded-xl p-6 max-w-3xl w-full border border-[#2A3A4D] max-h-[88vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Eye className="w-5 h-5 text-[#00E073]" /> Profil complet
                </h3>
                <Button variant="outline" size="sm" className="border-[#2A3A4D] text-[#94A3B8]"
                  onClick={() => setDetailModal(null)} data-testid="close-user-detail-modal">
                  <XCircle className="w-4 h-4" />
                </Button>
              </div>

              {(detailModal.loading || loadingDetail) ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#00E073]" /></div>
              ) : (
                <div className="space-y-6">
                  {/* Général */}
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wide mb-3">Informations générales</p>
                    <div className="bg-[#0D1C32] rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                      <div><span className="text-[#64748B]">Nom : </span><span className="text-white">{detailModal.name || "—"}</span></div>
                      <div><span className="text-[#64748B]">Type : </span><span className="text-white">{detailModal.user_type === "nurse" ? "Infirmier" : detailModal.user_type === "admin" ? "Admin" : "Patient"}</span></div>
                      <div><span className="text-[#64748B]">Email : </span><span className="text-white">{detailModal.email || "—"}</span></div>
                      <div><span className="text-[#64748B]">Téléphone : </span><span className="text-white">{detailModal.phone || "—"}</span></div>
                      <div><span className="text-[#64748B]">Nationalité : </span><span className="text-white">{detailModal.nationality || "—"}</span></div>
                      <div><span className="text-[#64748B]">Email vérifié : </span><span className={detailModal.email_verified ? "text-[#00E073]" : "text-yellow-500"}>{detailModal.email_verified ? "Oui" : "Non"}</span></div>
                      {detailModal.user_type === "patient" && (
                        <div><span className="text-[#64748B]">Assurance Maladie FR : </span><span className="text-white">{detailModal.affilie_secu_fr === true ? "Affilié" : detailModal.affilie_secu_fr === false ? "Non affilié (étranger)" : "Non renseigné"}</span></div>
                      )}
                      <div><span className="text-[#64748B]">Inscrit le : </span><span className="text-white">{detailModal.created_at ? new Date(detailModal.created_at).toLocaleDateString('fr-FR') : "—"}</span></div>
                      <div className="sm:col-span-2"><span className="text-[#64748B]">ID : </span><span className="text-white font-mono text-xs">{detailModal.user_id}</span></div>
                    </div>
                  </div>

                  {/* Infirmier */}
                  {detailModal.user_type === "nurse" && detailModal.nurse_profile && (() => {
                    const np = detailModal.nurse_profile;
                    const DAYS = [["lundi","Lundi"],["mardi","Mardi"],["mercredi","Mercredi"],["jeudi","Jeudi"],["vendredi","Vendredi"],["samedi","Samedi"],["dimanche","Dimanche"]];
                    const SLOTS = [["matin","Matin"],["apres_midi","Après-midi"],["soir","Soir"],["nuit","Nuit"]];
                    const av = np.availability || {};
                    return (
                      <>
                        {/* Statut & RPPS */}
                        <div>
                          <p className="text-xs text-[#64748B] uppercase tracking-wide mb-3">Statut & RPPS</p>
                          <div className="bg-[#0D1C32] rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                            <div><span className="text-[#64748B]">Compte : </span><span className={np.is_active ? "text-[#00E073]" : "text-yellow-500"}>{np.is_active ? "Actif" : "En attente"}</span></div>
                            <div><span className="text-[#64748B]">Onboarding : </span><span className="text-white">Étape {np.onboarding_step}/2</span></div>
                            <div><span className="text-[#64748B]">Conventionnement : </span><span className="text-white">{np.conventionne === true ? "Conventionné" : np.conventionne === false ? "Non conventionné" : "Non renseigné"}</span></div>
                            <div><span className="text-[#64748B]">N° RPPS : </span><span className="text-white">{np.rpps_number || "—"}</span></div>
                            <div><span className="text-[#64748B]">Statut RPPS : </span><span className="text-white">{np.rpps_status || "—"}</span></div>
                            <div className="sm:col-span-2"><span className="text-[#64748B]">Dernier contrôle RPPS : </span><span className="text-white">{np.rpps_last_checked_at ? new Date(np.rpps_last_checked_at).toLocaleString('fr-FR') : "—"}</span></div>
                            {np.rejection_reason && <div className="sm:col-span-2"><span className="text-[#64748B]">Motif de rejet : </span><span className="text-red-400">{np.rejection_reason}</span></div>}
                          </div>
                        </div>

                        {/* Entité juridique */}
                        <div>
                          <p className="text-xs text-[#64748B] uppercase tracking-wide mb-3">Entité juridique</p>
                          <div className="bg-[#0D1C32] rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                            <div><span className="text-[#64748B]">Raison sociale : </span><span className="text-white">{np.business_info?.business_name || "—"}</span></div>
                            <div><span className="text-[#64748B]">SIRET : </span><span className="text-white">{np.business_info?.siret || "—"}</span></div>
                            <div><span className="text-[#64748B]">N° TVA : </span><span className="text-white">{np.business_info?.tva_number || "—"}</span></div>
                            <div className="sm:col-span-2"><span className="text-[#64748B]">Adresse : </span><span className="text-white">{[np.business_info?.billing_address, np.business_info?.billing_postal_code, np.business_info?.billing_city].filter(Boolean).join(", ") || "—"}</span></div>
                          </div>
                        </div>

                        {/* Zones & tarifs */}
                        <div>
                          <p className="text-xs text-[#64748B] uppercase tracking-wide mb-3">Zones & tarifs</p>
                          <div className="bg-[#0D1C32] rounded-lg p-4 space-y-2 text-sm">
                            <div><span className="text-[#64748B]">Zones : </span><span className="text-white">{(np.zones || []).length ? np.zones.map(z => typeof z === "string" ? z : `${z.city}${z.radius ? ` (${z.radius} km)` : ""}`).join(", ") : "—"}</span></div>
                            <div className="grid grid-cols-3 gap-3 pt-1">
                              <div><span className="text-[#64748B]">Base : </span><span className="text-white">{np.base_rate ?? "—"} €</span></div>
                              <div><span className="text-[#64748B]">Nuit : </span><span className="text-white">+{np.night_surcharge ?? "—"} €</span></div>
                              <div><span className="text-[#64748B]">Week-end : </span><span className="text-white">+{np.weekend_surcharge ?? "—"} €</span></div>
                            </div>
                            {(np.skills || []).length > 0 && <div><span className="text-[#64748B]">Compétences : </span><span className="text-white">{np.skills.join(", ")}</span></div>}
                            {np.description && <div><span className="text-[#64748B]">Description : </span><span className="text-white">{np.description}</span></div>}
                          </div>
                        </div>

                        {/* Disponibilités */}
                        <div>
                          <p className="text-xs text-[#64748B] uppercase tracking-wide mb-3">Disponibilités</p>
                          <div className="bg-[#0D1C32] rounded-lg p-4 space-y-1.5 text-sm">
                            {DAYS.map(([d, dl]) => {
                              const slots = SLOTS.filter(([s]) => av[`${d}_${s}`] === true).map(([, sl]) => sl);
                              return (
                                <div key={d} className="flex gap-2">
                                  <span className="text-[#64748B] w-24 shrink-0">{dl} :</span>
                                  <span className={slots.length ? "text-white" : "text-[#64748B]"}>{slots.length ? slots.join(", ") : "Indisponible"}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Paiement & avis */}
                        <div>
                          <p className="text-xs text-[#64748B] uppercase tracking-wide mb-3">Paiement & avis</p>
                          <div className="bg-[#0D1C32] rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                            <div><span className="text-[#64748B]">Stripe : </span><span className={np.stripe?.connected ? "text-[#00E073]" : "text-yellow-500"}>{np.stripe?.simulated ? "Compte simulé" : np.stripe?.connected ? "Configuré" : "Non configuré"}</span></div>
                            <div><span className="text-[#64748B]">Paiements activés : </span><span className={np.stripe?.payouts_enabled ? "text-[#00E073]" : "text-yellow-500"}>{np.stripe?.payouts_enabled ? "Oui" : "Non"}</span></div>
                            <div><span className="text-[#64748B]">Note moyenne : </span><span className="text-white">{np.rating_average != null ? `${np.rating_average}/5` : "—"}</span></div>
                            <div><span className="text-[#64748B]">Nombre d'avis : </span><span className="text-white">{np.rating_count ?? 0}</span></div>
                            {np.stripe?.account_id && <div className="sm:col-span-2"><span className="text-[#64748B]">Compte Stripe : </span><span className="text-white font-mono text-xs">{np.stripe.account_id}</span></div>}
                          </div>
                        </div>

                        {/* Pièces justificatives */}
                        <div>
                          <p className="text-xs text-[#64748B] uppercase tracking-wide mb-3">Pièces justificatives</p>
                          <div className="bg-[#0D1C32] rounded-lg p-4 space-y-2 text-sm">
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-[#94A3B8]">Pièce d'identité {np.documents?.identity_number ? `(n° ${np.documents.identity_number})` : ""}</span>
                              {np.documents?.has_identity_file ? (
                                <Button size="sm" variant="outline" className="border-[#00E073]/40 text-[#00E073] hover:bg-[#00E073]/10"
                                  onClick={() => openIdentityDoc(detailModal.user_id)} data-testid="view-identity-doc-btn">
                                  <FileText className="w-4 h-4 mr-1" /> Consulter
                                </Button>
                              ) : <span className="text-[#64748B]">Non fournie</span>}
                            </div>
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-[#94A3B8]">Attestation RCP</span>
                              {np.documents?.has_rcp_file ? (
                                <Button size="sm" variant="outline" className="border-[#00E073]/40 text-[#00E073] hover:bg-[#00E073]/10"
                                  onClick={() => openRcpDoc(detailModal.user_id)} data-testid="view-rcp-doc-btn">
                                  <FileText className="w-4 h-4 mr-1" /> Consulter
                                </Button>
                              ) : np.documents?.rcp_url ? (
                                <a href={np.documents.rcp_url} target="_blank" rel="noopener noreferrer" className="text-[#00E073] hover:underline flex items-center gap-1 text-sm">
                                  <FileText className="w-4 h-4" /> Consulter
                                </a>
                              ) : np.documents?.has_rcp ? <span className="text-[#00E073]">Fournie</span> : <span className="text-[#64748B]">Non fournie</span>}
                            </div>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Revenue Details Modal */}
        {revenueModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="revenue-modal">
            <div className="bg-[#1F2A38] rounded-xl p-6 max-w-3xl w-full border border-[#2A3A4D] max-h-[85vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Détail des revenus</h3>
                <Button variant="outline" size="sm" className="border-[#2A3A4D] text-[#94A3B8]"
                  onClick={() => setRevenueModal(false)} data-testid="close-revenue-modal">
                  <XCircle className="w-4 h-4" />
                </Button>
              </div>

              {loadingRevenue ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#00E073]" /></div>
              ) : revenueDetails ? (
                <>
                  <div className="grid grid-cols-3 gap-3 mb-5">
                    <div className="bg-[#0D1C32] rounded-lg p-3">
                      <p className="text-xs text-[#64748B]">Prestations payées</p>
                      <p className="text-xl font-bold text-white">{revenueDetails.count}</p>
                    </div>
                    <div className="bg-[#0D1C32] rounded-lg p-3">
                      <p className="text-xs text-[#64748B]">Volume encaissé</p>
                      <p className="text-xl font-bold text-[#28CAD4]">{revenueDetails.total_volume?.toFixed(2)} €</p>
                    </div>
                    <div className="bg-[#0D1C32] rounded-lg p-3">
                      <p className="text-xs text-[#64748B]">Revenus plateforme</p>
                      <p className="text-xl font-bold text-[#00E073]">{revenueDetails.total_revenue?.toFixed(2)} €</p>
                    </div>
                  </div>

                  {revenueDetails.items?.length === 0 ? (
                    <div className="empty-state"><Euro className="empty-state-icon" /><p className="empty-state-text">Aucune prestation payée pour le moment</p></div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="revenue-table">
                        <thead>
                          <tr className="text-left text-[#64748B] border-b border-[#2A3A4D]">
                            <th className="py-2 pr-3 font-medium">Date</th>
                            <th className="py-2 pr-3 font-medium">Patient</th>
                            <th className="py-2 pr-3 font-medium">Infirmier</th>
                            <th className="py-2 pr-3 font-medium">Prestation</th>
                            <th className="py-2 pr-3 font-medium text-right">Montant</th>
                            <th className="py-2 pr-3 font-medium text-right">Commission</th>
                          </tr>
                        </thead>
                        <tbody>
                          {revenueDetails.items.map((it) => (
                            <tr key={it.transaction_id} className="border-b border-[#1F2A38] text-[#E2E8F0]" data-testid={`revenue-row-${it.transaction_id}`}>
                              <td className="py-2 pr-3 whitespace-nowrap text-[#94A3B8]">{it.date || "—"}{it.time ? ` ${it.time}` : ""}</td>
                              <td className="py-2 pr-3">{it.patient_name}</td>
                              <td className="py-2 pr-3">{it.nurse_name}</td>
                              <td className="py-2 pr-3">{it.care_type}{it.city ? ` · ${it.city}` : ""}</td>
                              <td className="py-2 pr-3 text-right text-white">{it.amount?.toFixed(2)} €</td>
                              <td className="py-2 pr-3 text-right text-[#00E073] font-semibold">{it.platform_fee?.toFixed(2)} €</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>
        )}

        {/* Ratings Tab */}
        {activeTab === "interventions" && (
          <AdminCareRequests token={token} />
        )}

        {activeTab === "ratings" && (
          <div className="card-dark p-6">
            <h2 className="text-lg font-semibold text-white mb-4">
              Notations des patients ({ratings.length})
            </h2>
            
            {ratings.length === 0 ? (
              <div className="empty-state">
                <Star className="empty-state-icon" />
                <p className="empty-state-text">Aucune notation pour le moment</p>
              </div>
            ) : (
              <div className="space-y-4">
                {ratings.map((rating) => (
                  <div 
                    key={rating.rating_id}
                    className="bg-[#0D1C32] rounded-xl p-4 border border-[#2A3A4D]"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {/* Stars display */}
                          <div className="flex gap-0.5">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`w-5 h-5 ${
                                  star <= rating.rating 
                                    ? "fill-yellow-400 text-yellow-400" 
                                    : "text-[#2A3A4D]"
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-lg font-semibold text-white">{rating.rating}/5</span>
                        </div>
                        
                        <div className="space-y-1 text-sm">
                          <p className="text-[#94A3B8]">
                            <span className="text-[#64748B]">Infirmier :</span> {rating.nurse_name}
                          </p>
                          <p className="text-[#94A3B8]">
                            <span className="text-[#64748B]">Patient :</span> {rating.patient_name}
                          </p>
                          <p className="text-[#94A3B8]">
                            <span className="text-[#64748B]">Date :</span> {rating.intervention_date}
                          </p>
                        </div>
                      </div>
                      
                      <div className="text-right text-xs text-[#64748B] flex flex-col items-end gap-2">
                        <span>
                          {new Date(rating.created_at).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-[#28CAD4] text-[#28CAD4] hover:bg-[#28CAD4]/10 h-8 px-2"
                            onClick={() => openEditRating(rating)}
                            data-testid={`edit-rating-${rating.rating_id}`}
                          >
                            <Pencil className="w-3 h-3 mr-1" />
                            Modifier
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-500 text-red-500 hover:bg-red-500/10 h-8 px-2"
                            onClick={() => handleDeleteRating(rating)}
                            disabled={actionLoading === rating.rating_id}
                            data-testid={`delete-rating-${rating.rating_id}`}
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Supprimer
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Verifications Tab */}
        {activeTab === "verifications" && (
          <div className="card-dark p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <h2 className="text-lg font-semibold text-white">
                Infirmiers en attente de vérification
              </h2>
              <Button
                onClick={handleVerifyRpps}
                disabled={verifyingRpps}
                className="btn-secondary"
                data-testid="verify-rpps-btn"
              >
                {verifyingRpps ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                Vérifier les RPPS maintenant
              </Button>
            </div>
            {pendingNurses.length === 0 ? (
              <div className="empty-state">
                <CheckCircle className="empty-state-icon" />
                <p className="empty-state-text">Aucune vérification en attente</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingNurses.map((nurse) => (
                  <div 
                    key={nurse.nurse_id}
                    className="bg-[#0D1C32] rounded-xl p-4 border border-[#2A3A4D]"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-medium text-white">{nurse.user_name}</h3>
                        <p className="text-sm text-[#94A3B8] flex items-center gap-1.5">
                          <Mail className="w-3.5 h-3.5 text-[#64748B]" /> {nurse.user_email}
                        </p>
                        <p className="text-sm text-[#94A3B8] flex items-center gap-1.5 mt-0.5">
                          <Phone className="w-3.5 h-3.5 text-[#64748B]" /> {nurse.user_phone || "Non renseigné"}
                        </p>
                        
                        <div className="mt-3 space-y-1 text-sm">
                          <p className="text-[#64748B] flex items-center gap-2">
                            <span className="text-[#94A3B8]">RPPS:</span> {nurse.rpps_number}
                            {getRppsBadge(nurse.rpps_status)}
                          </p>
                          <p className="text-[#64748B]">
                            <span className="text-[#94A3B8]">Identité:</span> {nurse.identity_document}
                          </p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {nurse.identity_document_file && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-[#28CAD4] text-[#28CAD4] hover:bg-[#28CAD4]/10"
                                onClick={() => handleViewIdentityDocument(nurse)}
                                data-testid={`view-identity-${nurse.nurse_id}`}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Voir la pièce d'identité
                              </Button>
                            )}
                            {nurse.rcp_document_file && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-[#00E073] text-[#00E073] hover:bg-[#00E073]/10"
                                onClick={() => handleViewRcpDocument(nurse)}
                                data-testid={`view-rcp-${nurse.nurse_id}`}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Voir l'attestation RCP
                              </Button>
                            )}
                          </div>
                          {!nurse.identity_document_file && (
                            <p className="text-xs text-amber-400 mt-2">
                              ⚠️ Aucune pièce d'identité jointe par l'infirmier
                            </p>
                          )}
                          {!nurse.rcp_document_file && (
                            <p className="text-xs text-amber-400 mt-1">
                              ⚠️ Aucune attestation RCP jointe par l'infirmier
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button 
                          size="sm"
                          className="bg-[#00E073] text-[#0D1C32] hover:bg-[#00B85C]"
                          onClick={() => handleVerifyNurse(nurse.nurse_id, true)}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Valider
                        </Button>
                        <Button 
                          size="sm"
                          variant="outline"
                          className="border-red-500 text-red-500 hover:bg-red-500/10"
                          onClick={() => handleVerifyNurse(nurse.nurse_id, false)}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Refuser
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* RPPS Audit Tab */}
        {activeTab === "rpps-audit" && (
          <div className="card-dark p-6" data-testid="rpps-audit-section">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
              <div>
                <h2 className="text-lg font-semibold text-white">
                  Historique des contrôles RPPS
                </h2>
                <p className="text-sm text-[#94A3B8] mt-1">
                  Journal horodaté des vérifications auprès de l'Annuaire Santé (ANS) — preuve de conformité CNIL/ARS.
                </p>
              </div>
              <Button
                onClick={handleVerifyRpps}
                disabled={verifyingRpps}
                className="btn-secondary"
                data-testid="verify-rpps-btn-audit"
              >
                {verifyingRpps ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                Vérifier les RPPS maintenant
              </Button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-2 my-4">
              {[
                { id: "all", label: "Tous" },
                { id: "valid", label: "Valides" },
                { id: "inactive", label: "Radiés/inactifs" },
                { id: "not_found", label: "Introuvables" },
                { id: "error", label: "Erreurs" }
              ].map((f) => (
                <button
                  key={f.id}
                  onClick={() => setRppsFilter(f.id)}
                  data-testid={`rpps-filter-${f.id}`}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    rppsFilter === f.id
                      ? "bg-[#00E073] text-[#0D1C32]"
                      : "bg-[#1F2A38] text-[#94A3B8] hover:bg-[#2A3A4D]"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {(() => {
              const filtered = rppsFilter === "all"
                ? rppsChecks
                : rppsChecks.filter((c) => c.status === rppsFilter);
              const actionMap = {
                disabled: { cls: "bg-red-500/20 text-red-400", label: "Compte désactivé" },
                flagged: { cls: "bg-amber-500/20 text-amber-400", label: "Signalé" },
                none: { cls: "bg-[#2A3A4D] text-[#94A3B8]", label: "Aucune" }
              };
              const sourceMap = {
                daily_sweep: "Balayage quotidien",
                manual: "Manuel (admin)",
                onboarding: "Inscription"
              };
              const fmtDate = (iso) => {
                if (!iso) return "—";
                try {
                  return new Date(iso).toLocaleString("fr-FR", {
                    day: "2-digit", month: "2-digit", year: "numeric",
                    hour: "2-digit", minute: "2-digit"
                  });
                } catch { return iso; }
              };
              if (filtered.length === 0) {
                return (
                  <div className="empty-state">
                    <History className="empty-state-icon" />
                    <p className="empty-state-text">Aucun contrôle enregistré pour ce filtre</p>
                  </div>
                );
              }
              return (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="rpps-audit-table">
                    <thead>
                      <tr className="text-left text-[#64748B] border-b border-[#2A3A4D]">
                        <th className="py-2 pr-4 font-medium">Date & heure</th>
                        <th className="py-2 pr-4 font-medium">Infirmier</th>
                        <th className="py-2 pr-4 font-medium">N° RPPS</th>
                        <th className="py-2 pr-4 font-medium">Statut</th>
                        <th className="py-2 pr-4 font-medium">Action</th>
                        <th className="py-2 pr-4 font-medium">Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((c) => (
                        <tr
                          key={c.check_id}
                          className="border-b border-[#1F2A38] text-[#E2E8F0]"
                          data-testid={`rpps-check-row-${c.check_id}`}
                        >
                          <td className="py-2 pr-4 whitespace-nowrap text-[#94A3B8]">{fmtDate(c.checked_at)}</td>
                          <td className="py-2 pr-4">{c.nurse_name || "—"}</td>
                          <td className="py-2 pr-4 font-mono text-[#94A3B8]">{c.rpps_number || "—"}</td>
                          <td className="py-2 pr-4">{getRppsBadge(c.status)}</td>
                          <td className="py-2 pr-4">
                            {(() => {
                              const a = actionMap[c.action_taken] || actionMap.none;
                              return <span className={`px-2 py-0.5 rounded text-xs ${a.cls}`}>{a.label}</span>;
                            })()}
                          </td>
                          <td className="py-2 pr-4 text-[#94A3B8]">{sourceMap[c.source] || c.source || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-xs text-[#64748B] mt-3" data-testid="rpps-audit-count">
                    {filtered.length} contrôle(s) affiché(s)
                  </p>
                </div>
              );
            })()}
          </div>
        )}

        {/* Disputes Tab */}
        {activeTab === "disputes" && (          <div className="card-dark p-6">
            <h2 className="text-lg font-semibold text-white mb-4">
              Gestion des litiges
            </h2>
            
            {disputes.length === 0 ? (
              <div className="empty-state">
                <CheckCircle className="empty-state-icon" />
                <p className="empty-state-text">Aucun litige</p>
              </div>
            ) : (
              <div className="space-y-4">
                {disputes.map((dispute) => {
                  const patient = dispute.patient || {};
                  const nurse = dispute.nurse || {};
                  const intervention = dispute.intervention || {};
                  const patientFullName = [patient.first_name, patient.last_name].filter(Boolean).join(" ") || patient.name || "—";
                  const nurseFullName = [nurse.first_name, nurse.last_name].filter(Boolean).join(" ") || nurse.name || "—";
                  return (
                  <div 
                    key={dispute.dispute_id}
                    className="bg-[#0D1C32] rounded-xl p-4 border border-[#2A3A4D]"
                    data-testid={`dispute-card-${dispute.dispute_id}`}
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-[260px]">
                        <div className="flex items-center gap-2 mb-3 flex-wrap">
                          <h3 className="font-medium text-white capitalize">
                            {dispute.reason === "intervention_not_realized" ? "Intervention non réalisée" : dispute.reason}
                          </h3>
                          <span className={`status-badge ${
                            dispute.status === "open" ? "status-pending" :
                            dispute.status === "resolved" ? "status-confirmed" : "status-cancelled"
                          }`}>
                            {dispute.status === "open" ? "Ouvert" : dispute.status === "resolved" ? "Traité" : "Clôturé"}
                          </span>
                          {dispute.outcome && (
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              dispute.outcome === "refund_patient" ? "bg-orange-500/20 text-orange-300" : "bg-[#00E073]/20 text-[#00E073]"
                            }`} data-testid={`dispute-outcome-${dispute.dispute_id}`}>
                              {dispute.outcome === "refund_patient" ? "Remboursement au patient" : "Paiement au soignant"}
                            </span>
                          )}
                          {intervention.type && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300">
                              Ponctuelle
                            </span>
                          )}
                        </div>

                        {/* Motif déclaré */}
                        <div className="bg-[#1F2A38] rounded-lg p-3 mb-3 border-l-4 border-orange-400">
                          <p className="text-xs text-[#64748B] mb-1 uppercase tracking-wide">Motif déclaré par {dispute.initiator === "nurse" ? "l'infirmier" : "le patient"}</p>
                          <p className="text-sm text-[#E2E8F0] whitespace-pre-wrap">{dispute.description}</p>
                        </div>

                        {/* Réponse infirmier */}
                        {dispute.nurse_response ? (
                          <div className="bg-[#1F2A38] rounded-lg p-3 mb-3 border-l-4 border-emerald-400" data-testid={`admin-nurse-response-${dispute.dispute_id}`}>
                            <p className="text-xs text-[#64748B] mb-1 uppercase tracking-wide">
                              Réponse de l'infirmier {dispute.nurse_response_at ? `(${new Date(dispute.nurse_response_at).toLocaleString("fr-FR")})` : ""}
                            </p>
                            <p className="text-sm text-[#E2E8F0] whitespace-pre-wrap">{dispute.nurse_response}</p>
                          </div>
                        ) : (
                          <div className="bg-[#1F2A38] rounded-lg p-3 mb-3 border-l-4 border-[#2A3A4D]" data-testid={`admin-nurse-response-pending-${dispute.dispute_id}`}>
                            <p className="text-xs text-[#94A3B8]">⏳ En attente de la réponse de l'infirmier</p>
                          </div>
                        )}

                        {/* Contacts patient / infirmier */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                          <div className="bg-[#1F2A38] rounded-lg p-3">
                            <p className="text-xs text-[#64748B] mb-1 uppercase tracking-wide">Patient</p>
                            <p className="text-sm text-white font-medium" data-testid={`dispute-patient-name-${dispute.dispute_id}`}>{patientFullName}</p>
                            {patient.email && (
                              <a href={`mailto:${patient.email}`} className="text-xs text-[#00E073] hover:underline block mt-1" data-testid={`dispute-patient-email-${dispute.dispute_id}`}>
                                {patient.email}
                              </a>
                            )}
                            {patient.phone && (
                              <a href={`tel:${patient.phone}`} className="text-xs text-[#94A3B8] hover:text-white block">
                                {patient.phone}
                              </a>
                            )}
                          </div>
                          <div className="bg-[#1F2A38] rounded-lg p-3">
                            <p className="text-xs text-[#64748B] mb-1 uppercase tracking-wide">Infirmier</p>
                            <p className="text-sm text-white font-medium" data-testid={`dispute-nurse-name-${dispute.dispute_id}`}>{nurseFullName}</p>
                            {nurse.email && (
                              <a href={`mailto:${nurse.email}`} className="text-xs text-[#00E073] hover:underline block mt-1" data-testid={`dispute-nurse-email-${dispute.dispute_id}`}>
                                {nurse.email}
                              </a>
                            )}
                            {nurse.phone && (
                              <a href={`tel:${nurse.phone}`} className="text-xs text-[#94A3B8] hover:text-white block">
                                {nurse.phone}
                              </a>
                            )}
                          </div>
                        </div>

                        {/* Détails intervention */}
                        <div className="bg-[#1F2A38] rounded-lg p-3 mb-2">
                          <p className="text-xs text-[#64748B] mb-2 uppercase tracking-wide">Détails de la prestation</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
                            {intervention.date && (
                              <div>
                                <span className="text-[#94A3B8]">Date :</span> <span className="text-white">{intervention.date}{intervention.time ? ` à ${intervention.time}` : ""}</span>
                              </div>
                            )}
                            {intervention.start_date && (
                              <div className="sm:col-span-2">
                                <span className="text-[#94A3B8]">Période :</span>{" "}
                                <span className="text-white">{intervention.start_date} → {intervention.end_date}</span>
                              </div>
                            )}
                            {(intervention.address || intervention.city) && (
                              <div className="sm:col-span-2">
                                <span className="text-[#94A3B8]">Adresse :</span>{" "}
                                <span className="text-white">{[intervention.address, intervention.postal_code, intervention.city].filter(Boolean).join(", ")}</span>
                              </div>
                            )}
                            {intervention.amount != null && (
                              <div>
                                <span className="text-[#94A3B8]">Montant :</span> <span className="text-white">{Number(intervention.amount).toFixed(2)} €</span>
                              </div>
                            )}
                            {intervention.is_urgent && (
                              <div>
                                <span className="text-orange-400 font-medium">Demande urgente</span>
                              </div>
                            )}
                          </div>
                        </div>

                        <p className="text-xs text-[#64748B]">
                          Réf. {dispute.request_id} • Créé le {new Date(dispute.created_at).toLocaleString('fr-FR')}
                        </p>
                      </div>
                      
                      {dispute.status === "open" && (
                        <div className="flex gap-2 flex-col min-w-[200px]">
                          <Button 
                            size="sm"
                            className="bg-[#00E073] text-[#0D1C32] hover:bg-[#00B85C]"
                            onClick={() => handleResolveDispute(dispute.dispute_id, "resolved", "release_nurse")}
                            disabled={actionLoading === dispute.dispute_id}
                            data-testid={`release-nurse-${dispute.dispute_id}`}
                          >
                            Paiement au soignant
                          </Button>
                          <Button 
                            size="sm"
                            className="bg-orange-500 text-white hover:bg-orange-600"
                            onClick={() => handleResolveDispute(dispute.dispute_id, "resolved", "refund_patient")}
                            disabled={actionLoading === dispute.dispute_id}
                            data-testid={`refund-patient-${dispute.dispute_id}`}
                          >
                            Remboursement au patient
                          </Button>
                          <Button 
                            size="sm"
                            variant="outline"
                            className="border-[#2A3A4D] text-[#94A3B8] hover:bg-[#2A3A4D]"
                            onClick={() => handleResolveDispute(dispute.dispute_id, "resolved")}
                            disabled={actionLoading === dispute.dispute_id}
                            data-testid={`resolve-dispute-${dispute.dispute_id}`}
                          >
                            Clôturer sans action financière
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Edit Rating Modal */}
      {editRatingModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#1F2A38] border border-[#2A3A4D] rounded-xl p-6 max-w-md w-full" data-testid="edit-rating-modal">
            <h3 className="text-lg font-semibold text-white mb-2">Modifier la notation</h3>
            <p className="text-sm text-[#94A3B8] mb-4">
              {editRatingModal.patient_name} → {editRatingModal.nurse_name}
            </p>

            <div className="mb-4">
              <Label className="form-label">Note (1-5 étoiles)</Label>
              <div className="flex gap-1 mt-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setEditRatingValue(star)}
                    className="hover:scale-110 transition-transform"
                    data-testid={`star-${star}`}
                  >
                    <Star
                      className={`w-8 h-8 ${
                        star <= editRatingValue
                          ? "text-yellow-400 fill-yellow-400"
                          : "text-[#2A3A4D]"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setEditRatingModal(null)}
                className="border-[#2A3A4D] text-[#94A3B8]"
              >
                Annuler
              </Button>
              <Button
                onClick={handleUpdateRating}
                disabled={actionLoading === editRatingModal.rating_id}
                className="bg-[#00E073] text-[#0D1C32] hover:bg-[#00B85C]"
                data-testid="confirm-edit-rating-btn"
              >
                {actionLoading === editRatingModal.rating_id ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
